import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Fade,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  setOverlayChromePrimed,
  useThemeColorOverlay,
} from '../../hooks/use-theme-color/use-theme-color';
import { Button } from '../button/button';
import { SteppedProgress } from '../stepped-progress';
import { MODAL_TRANSITION_TIMEOUT, modalDropInSx } from './modal-transition';
import { newFlowId } from '@farmshare/utils';
import {
  analyticsErrorType,
  analyticsStepKey,
  FlowCancelVia,
  ModalAnalyticsParams,
  useModalAnalyticsEmitter,
} from './modal-analytics';

type CancelVia = FlowCancelVia;

export interface IMultiStepActions<TData = Record<string, unknown>> {
  next: () => void;
  back: () => void;
  goToStep: (stepIndex: number) => void;
  updateData: (updates: Partial<TData>) => void;
  getData: () => TData;
  getCurrentStep: () => number;
  getTotalSteps: () => number;
  close: () => void;
}

export interface IMultiStepModalStep<TData = Record<string, unknown>> {
  title: string | ReactNode;
  /**
   * Stable funnel identifier for this step (snake_case). Falls back to a slug
   * of a string `title`, then `step_<index>`. Pass one whenever the title is a
   * node or might be reworded — renaming a key breaks historical funnels.
   */
  analyticsKey?: string;
  body: (data: TData, actions: IMultiStepActions<TData>) => ReactNode;
  canGoNext?: (data: TData) => boolean;
  canGoBack?: boolean;
  hideBackButton?: boolean;
  hideNextButton?: boolean;
  /**
   * Static label, or a function of the current data when the label depends on
   * state (e.g. a Review step that reads "Next" while a later step is visible
   * and "Submit" once that step is hidden).
   */
  nextButtonText?: string | ((data: TData) => string);
  backButtonText?: string;
  /**
   * Conditionally include this step based on the current data. A step that
   * returns `false` is skipped during navigation and omitted from the progress
   * indicator, so it can appear/disappear as the user edits earlier steps.
   * Steps without a predicate are always visible (unchanged behavior).
   */
  visible?: (data: TData) => boolean;
  onNext?: (data: TData) => void | Promise<void>;
  onBack?: (data: TData) => void | Promise<void>;
}

export interface IMultiStepModal<TData = Record<string, unknown>> {
  /**
   * Funnel name for this wizard (snake_case, e.g. `quick_schedule`). Required
   * so no wizard ships unmeasured: the renderer reports start, every step
   * viewed/completed/backed out of, and how the run ended, all under this name.
   */
  analyticsName: string;
  /**
   * Extra dimensions stamped on every event of the run — typically where the
   * wizard was opened from (`{ entry: 'calendar' }`). Never put customer data
   * here; the host app drops keys it doesn't recognise.
   */
  analyticsContext?: ModalAnalyticsParams;
  title?: string | ReactNode;
  steps: IMultiStepModalStep<TData>[];
  initialData?: TData;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Force fullscreen at every breakpoint. */
  fullScreen?: boolean;
  /**
   * Go fullscreen only on small screens (< sm), staying centered on larger.
   *
   * Defaults ON: a multi-step modal is a wizard — step content, a progress
   * indicator and a footer of actions — and there is no phone on which that
   * fits comfortably inside a centered card. Pass `false` for the rare short
   * one that should stay a card.
   */
  fullScreenOnMobile?: boolean;
  disableBackdropClick?: boolean;
  /**
   * Runs on the last step's Next. Resolve to close the wizard as completed;
   * throw — or resolve `false` when the failure has already been shown (a
   * toast) — to keep it open on that step, reported as a `step_error` rather
   * than a completion.
   */
  onComplete?: (data: TData) => void | boolean | Promise<void | boolean>;
  onCancel?: (data: TData) => void;
  dialogProps?: Partial<DialogProps>;
  /**
   * Accessible name for a wizard with no title bar. It goes on the paper — the
   * element with role="dialog" — because an `aria-label` passed through
   * `dialogProps` lands on the presentation root, where nothing reads it.
   */
  ariaLabel?: string;
  /** Show default stepped progress indicator. Default: true */
  showProgress?: boolean;
  /** Custom progress renderer. Overrides showProgress if provided */
  renderProgress?: (currentStep: number, totalSteps: number) => ReactNode;
  /** Optional error handler for step progression failures */
  onError?: (error: Error, context: 'next' | 'back' | 'complete') => void;
  /**
   * Render a Cancel button on the left of the footer when on the first step
   * (where the Back button doesn't apply). Useful when the title/close-X is
   * suppressed (e.g. fullscreen mobile) so the user still has an exit.
   */
  showCancelOnFirstStep?: boolean;
  /** Text for the Cancel button (default 'Cancel'). */
  cancelButtonText?: string;
}

interface IMultiStepModalRendererProps<TData = Record<string, unknown>> {
  modal: IMultiStepModal<TData>;
  onClose: () => void;
}

// Context to share data and actions across step components
// Context must hold IMultiStepActions<TData> for any TData; `unknown` breaks
// structural assignability of the Provider's `value` prop across the generic
// parameter, so this stays `any` (narrowed back to TData in useMultiStepModal).
const MultiStepContext = createContext<IMultiStepActions<any> | null>(null);

export const useMultiStepModal = <TData = Record<string, unknown>,>() => {
  const context = useContext(MultiStepContext);
  if (!context) {
    throw new Error('useMultiStepModal must be used within a MultiStepModal');
  }
  return context as IMultiStepActions<TData>;
};

export default function MultiStepModalRenderer<
  TData = Record<string, unknown>,
>({ modal, onClose }: IMultiStepModalRendererProps<TData>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<TData>(modal.initialData || ({} as TData));
  const [isLoading, setIsLoading] = useState(false);

  const muiTheme = useTheme();
  const isMobileScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const fullScreen =
    modal.fullScreen || ((modal.fullScreenOnMobile ?? true) && isMobileScreen);

  // Always registered while mounted; only fullscreen names a colour — see
  // modal-renderer for why presence and colour are tracked separately.
  useThemeColorOverlay(
    true,
    fullScreen ? muiTheme.palette.background.paper : undefined,
  );

  // Dim the chrome for as long as we're mounted, and release on unmount. This
  // renderer is mounted directly rather than through the provider's setModal,
  // so nothing else primes or un-primes on our behalf.
  useEffect(() => {
    setOverlayChromePrimed(true);
    return () => setOverlayChromePrimed(false);
  }, []);

  // Synchronous in-flight guard. `isLoading` is React state, so the footer
  // button's `disabled={isLoading}` only takes effect after a re-render — two
  // clicks (or Enter-then-click) dispatched in the same tick both enter
  // `next()` before the disable commits, double-firing `onComplete`. For a
  // step whose completion CREATES data (e.g. processorSchedulingCreate) that
  // double-fire persists a full duplicate record set. This ref is set/checked
  // synchronously so the second call bails immediately.
  const inFlightRef = useRef(false);
  const prevStepRef = useRef(currentStep);

  // Update previous step reference after render
  useEffect(() => {
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // Calculate slide direction based on previous step
  const slideDirection: 'left' | 'right' =
    currentStep > prevStepRef.current ? 'left' : 'right';

  // Original indices of the steps currently visible for this data. Steps with a
  // `visible` predicate that returns false drop out of navigation and the
  // progress bar, so the flow length reacts to what the user has entered.
  const visibleStepIndices = useMemo(
    () =>
      modal.steps
        .map((step, index) =>
          !step.visible || step.visible(data) ? index : -1,
        )
        .filter((index) => index >= 0),
    [modal.steps, data],
  );

  // If the step we're on becomes invisible (e.g. the user went back and changed
  // an answer that hides it), snap to the nearest still-visible step at or
  // before it so we never render a hidden step.
  useEffect(() => {
    if (
      visibleStepIndices.length > 0 &&
      !visibleStepIndices.includes(currentStep)
    ) {
      const fallback =
        [...visibleStepIndices].reverse().find((i) => i <= currentStep) ??
        visibleStepIndices[0];
      setCurrentStep(fallback);
    }
  }, [visibleStepIndices, currentStep]);

  const currentStepConfig = modal.steps[currentStep];
  const currentVisiblePosition = visibleStepIndices.indexOf(currentStep);
  const isFirstStep = currentVisiblePosition <= 0;
  const isLastStep = currentVisiblePosition === visibleStepIndices.length - 1;

  // ---- Funnel analytics ---------------------------------------------------
  // Every run reports flow_start, a step_view per step shown, step_complete /
  // step_back / step_error as the user moves, and exactly one ending:
  // flow_complete, flow_cancel (the user dismissed it) or flow_abandon (it
  // went away some other way — a route change, the tab closing). See
  // modal-analytics.ts for where the events go.
  const emitAnalytics = useModalAnalyticsEmitter();
  const flowIdRef = useRef<string>('');
  if (!flowIdRef.current) flowIdRef.current = newFlowId();
  const flowStartedAtRef = useRef(Date.now());
  const stepEnteredAtRef = useRef(Date.now());
  const startedRef = useRef(false);
  const lastViewedStepRef = useRef<number | null>(null);
  const stepsViewedRef = useRef(new Set<number>());
  // Set when the run ends, so only one ending is ever reported.
  const outcomeRef = useRef<'complete' | 'cancel' | 'abandon' | null>(null);

  const trackFlow = useCallback(
    (name: Parameters<typeof emitAnalytics>[0], params: ModalAnalyticsParams) =>
      emitAnalytics(name, {
        flow: modal.analyticsName,
        flow_id: flowIdRef.current,
        ...modal.analyticsContext,
        ...params,
      }),
    [emitAnalytics, modal.analyticsName, modal.analyticsContext],
  );

  const stepParams = useCallback(
    (stepIndex: number): ModalAnalyticsParams => ({
      step: analyticsStepKey(modal.steps[stepIndex] ?? {}, stepIndex),
      step_index: stepIndex,
      step_position: visibleStepIndices.indexOf(stepIndex) + 1,
      visible_total: visibleStepIndices.length,
    }),
    [modal.steps, visibleStepIndices],
  );
  const msOnStep = useCallback(() => Date.now() - stepEnteredAtRef.current, []);

  const trackCancel = useCallback(
    (via: CancelVia) => {
      if (outcomeRef.current) return;
      outcomeRef.current = 'cancel';
      trackFlow('flow_cancel', {
        ...stepParams(currentStep),
        via,
        ms_on_step: msOnStep(),
        ms_total: Date.now() - flowStartedAtRef.current,
      });
    },
    [trackFlow, stepParams, currentStep, msOnStep],
  );

  // The unmount cleanup and the pagehide listener are registered once, so they
  // read the current step through this ref rather than a stale closure.
  const latestAnalyticsRef = useRef({ trackFlow, stepParams, currentStep });
  latestAnalyticsRef.current = { trackFlow, stepParams, currentStep };
  const abandonTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    // StrictMode mounts, unmounts and remounts in development. The remount
    // cancels the abandon its own cleanup just scheduled, and `startedRef`
    // keeps flow_start from firing twice.
    clearTimeout(abandonTimerRef.current);
    if (!startedRef.current) {
      startedRef.current = true;
      latestAnalyticsRef.current.trackFlow('flow_start', {
        visible_total: visibleStepIndices.length,
      });
    }

    const endAsAbandoned = (reason: 'unmount' | 'page_hide') => {
      if (outcomeRef.current) return;
      outcomeRef.current = 'abandon';
      const latest = latestAnalyticsRef.current;
      latest.trackFlow('flow_abandon', {
        ...latest.stepParams(latest.currentStep),
        reason,
        ms_on_step: Date.now() - stepEnteredAtRef.current,
        ms_total: Date.now() - flowStartedAtRef.current,
      });
    };
    const onPageHide = () => endAsAbandoned('page_hide');
    // Back/forward cache: the page — wizard and all — comes back after its
    // pagehide was reported as an abandon. What the user does next is a new
    // run, so start one rather than letting it end a second time.
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || outcomeRef.current !== 'abandon') return;
      const latest = latestAnalyticsRef.current;
      flowIdRef.current = newFlowId();
      outcomeRef.current = null;
      flowStartedAtRef.current = Date.now();
      stepEnteredAtRef.current = Date.now();
      stepsViewedRef.current = new Set([latest.currentStep]);
      lastViewedStepRef.current = latest.currentStep;
      const params = latest.stepParams(latest.currentStep);
      latest.trackFlow('flow_start', { visible_total: params.visible_total });
      latest.trackFlow('step_view', params);
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      abandonTimerRef.current = setTimeout(() => endAsAbandoned('unmount'), 0);
    };
    // Mount/unmount only — everything that changes is read via the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // A step that just went invisible is about to be snapped away from (see
    // above); report the step the user actually lands on instead.
    if (!visibleStepIndices.includes(currentStep)) return;
    if (lastViewedStepRef.current === currentStep) return;
    lastViewedStepRef.current = currentStep;
    stepEnteredAtRef.current = Date.now();
    stepsViewedRef.current.add(currentStep);
    trackFlow('step_view', stepParams(currentStep));
  }, [currentStep, visibleStepIndices, stepParams, trackFlow]);

  const updateData = useCallback((updates: Partial<TData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const getData = useCallback(() => data, [data]);

  const getCurrentStep = useCallback(() => currentStep, [currentStep]);

  const getTotalSteps = useCallback(
    () => modal.steps.length,
    [modal.steps.length],
  );

  // A step closing the wizard itself. Reported as a cancel `via: 'step'` so it
  // stays distinguishable from the user dismissing it.
  const close = useCallback(() => {
    trackCancel('step');
    onClose();
  }, [onClose, trackCancel]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < modal.steps.length) {
        setCurrentStep(stepIndex);
      }
    },
    [modal.steps.length],
  );

  const next = useCallback(async () => {
    // Bail synchronously if a step transition (which may run onComplete's
    // create) is already in flight — see `inFlightRef` above.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsLoading(true);
    // Which call threw: a last step's `onNext` is still validation, not the
    // completion itself.
    let phase: 'next' | 'complete' = 'next';
    try {
      if (currentStepConfig.onNext) {
        await currentStepConfig.onNext(data);
      }

      const position = visibleStepIndices.indexOf(currentStep);
      const nextStepIndex = visibleStepIndices[position + 1];
      if (nextStepIndex === undefined) {
        phase = 'complete';
        const completed = modal.onComplete
          ? await modal.onComplete(data)
          : undefined;
        if (completed === false) {
          // onComplete reported its own failure (and kept the user's input):
          // stay open on this step so they can retry.
          trackFlow('step_error', {
            ...stepParams(currentStep),
            phase,
            error_type: 'not_completed',
          });
          return;
        }
        // A run already ended (a bfcache-restored page whose pagehide was
        // reported as an abandon) can't end twice.
        if (outcomeRef.current) {
          onClose();
          return;
        }
        // The last step only counts as completed once onComplete has resolved.
        outcomeRef.current = 'complete';
        trackFlow('step_complete', {
          ...stepParams(currentStep),
          ms_on_step: msOnStep(),
        });
        trackFlow('flow_complete', {
          steps_viewed: stepsViewedRef.current.size,
          visible_total: visibleStepIndices.length,
          ms_total: Date.now() - flowStartedAtRef.current,
        });
        onClose();
      } else {
        trackFlow('step_complete', {
          ...stepParams(currentStep),
          ms_on_step: msOnStep(),
        });
        setCurrentStep(nextStepIndex);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in step progression:', error);
      trackFlow('step_error', {
        ...stepParams(currentStep),
        phase,
        error_type: analyticsErrorType(error),
      });
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          isLastStep ? 'complete' : 'next',
        );
      }
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [
    currentStepConfig,
    data,
    isLastStep,
    modal,
    onClose,
    visibleStepIndices,
    currentStep,
    trackFlow,
    stepParams,
    msOnStep,
  ]);

  const back = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepConfig.onBack) {
        await currentStepConfig.onBack(data);
      }
      const position = visibleStepIndices.indexOf(currentStep);
      const prevStepIndex = visibleStepIndices[position - 1];
      if (prevStepIndex !== undefined) {
        trackFlow('step_back', {
          ...stepParams(currentStep),
          ms_on_step: msOnStep(),
        });
        setCurrentStep(prevStepIndex);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error going back:', error);
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          'back',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    currentStepConfig,
    data,
    modal,
    visibleStepIndices,
    currentStep,
    trackFlow,
    stepParams,
    msOnStep,
  ]);

  // The close button, the backdrop, Escape and the first step's Cancel all come
  // through here. None of them may dismiss the wizard while a step transition
  // is in flight: the last one runs `onComplete`, and closing over that leaves
  // (say) a customer merge running with no dialog to report how it ended.
  // `actions.close()` and the close after a successful `onComplete` call
  // `onClose` directly, so they are unaffected.
  const handleClose = useCallback(
    (via: CancelVia) => {
      if (inFlightRef.current) return;
      trackCancel(via);
      if (modal.onCancel) {
        modal.onCancel(data);
      }
      onClose();
    },
    [modal, data, onClose, trackCancel],
  );

  const canGoNext = useMemo(() => {
    if (currentStepConfig.canGoNext) {
      return currentStepConfig.canGoNext(data);
    }
    return true;
  }, [currentStepConfig, data]);

  const canGoBack = useMemo(() => {
    if (currentStepConfig.canGoBack !== undefined) {
      return currentStepConfig.canGoBack;
    }
    return !isFirstStep;
  }, [currentStepConfig, isFirstStep]);

  const actions: IMultiStepActions<TData> = useMemo(
    () => ({
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    }),
    [
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    ],
  );

  // The first step renders statically — only animate the slide once the user
  // has navigated between steps. `prevStepRef` (updated in an effect after
  // render) still holds the previous step during the render where currentStep
  // changes, so a mismatch here means a navigation just happened.
  const hasNavigatedRef = useRef(false);
  if (currentStep !== prevStepRef.current) {
    hasNavigatedRef.current = true;
  }

  let progressContent: ReactNode = null;
  if (modal.renderProgress) {
    progressContent = (
      <Box sx={{ px: 5, py: 1 }}>
        {modal.renderProgress(
          Math.max(0, currentVisiblePosition) + 1,
          visibleStepIndices.length,
        )}
      </Box>
    );
  } else if (modal.showProgress !== false) {
    progressContent = (
      <Box
        sx={
          modal.title
            ? { pt: 0, px: 1, pb: 1 }
            : {
                p: 0,
                // SteppedProgress applies its own 4px top/bottom padding;
                // strip it so the chevron sits flush as a true header.
                '& [role="progressbar"]': { padding: 0 },
                // Headerless + fullscreen makes this the topmost element, so it
                // inherits the DialogTitle's job of clearing the notch. Padding
                // sits on the wrapper so the chevrons stay flush to the edges
                // (Safari reports a 0 inset, making this a no-op there).
                ...(fullScreen && { pt: 'env(safe-area-inset-top)' }),
              }
        }
      >
        <SteppedProgress
          steps={visibleStepIndices.map((index) => {
            const { title } = modal.steps[index];
            return typeof title === 'string' ? title : 'Step';
          })}
          currentStep={Math.max(0, currentVisiblePosition) + 1}
          rounded={Boolean(modal.title)}
          // Headerless: square-cornered and full-bleed so it reads as the
          // dialog's own header bar, at the same 36px the cutsheet catalogue
          // wizard uses. With a title it's an in-content indicator, so it stays
          // rounded and short.
          height={modal.title ? 24 : 36}
          compact={isMobileScreen}
        />
      </Box>
    );
  }

  return (
    <MultiStepContext.Provider value={actions}>
      <Dialog
        open
        onClose={
          modal.disableBackdropClick
            ? undefined
            : (_event, reason) =>
                handleClose(reason === 'escapeKeyDown' ? 'escape' : 'backdrop')
        }
        maxWidth={modal.size || 'md'}
        fullWidth
        fullScreen={fullScreen}
        // Quick fade in + a subtle 20px drop.
        slots={{ transition: Fade }}
        slotProps={{
          transition: { timeout: MODAL_TRANSITION_TIMEOUT },
          paper: {
            // See modal-renderer: the provider owns its own mobile sizing, so
            // it opts out of the theme's full-bleed-on-mobile rule.
            className: fullScreen ? undefined : 'fs-dialog-compact',
            sx: modalDropInSx,
            ...(modal.ariaLabel && { 'aria-label': modal.ariaLabel }),
          },
        }}
        {...modal.dialogProps}
      >
        {modal.title && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.5,
              // Fullscreen means the paper runs edge to edge, so the title is
              // under the status bar / notch when installed to the home screen
              // (in Safari the inset is 0 and this is a no-op).
              ...(fullScreen && { pt: 'calc(env(safe-area-inset-top) + 4px)' }),
            }}
          >
            {modal.title}
            <IconButton
              aria-label="close"
              onClick={() => handleClose('close_button')}
              disabled={isLoading}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
        )}
        {progressContent}

        <DialogContent
          sx={{
            minHeight: 200,
            overflow: 'scroll',
            position: 'relative',
            pt: 1,
            pb: 1,
            // MUI's default 24px horizontal padding wastes too much room on
            // small/full-screen surfaces; tighten when fullScreen.
            ...(fullScreen && { px: 1.5 }),
          }}
        >
          <Slide
            direction={slideDirection}
            in
            appear={hasNavigatedRef.current}
            mountOnEnter
            unmountOnExit
            key={currentStep}
          >
            <Box>{currentStepConfig.body(data, actions)}</Box>
          </Slide>
        </DialogContent>

        <DialogActions
          sx={{
            // Same edge-to-edge problem at the bottom: Back/Next would sit under
            // the iOS home indicator (and behind Safari's toolbar) without the
            // inset. 8px is MUI's own DialogActions padding, kept on top of it.
            ...(fullScreen && {
              px: 1.5,
              pb: 'calc(env(safe-area-inset-bottom) + 8px)',
            }),
          }}
        >
          {modal.showCancelOnFirstStep && isFirstStep && (
            <Button
              content={modal.cancelButtonText || 'Cancel'}
              variant="ghost"
              onClick={() => handleClose('cancel_button')}
              disabled={isLoading}
              sx={{ mr: 'auto' }}
            />
          )}

          {!currentStepConfig.hideBackButton && canGoBack && (
            <Button
              content={currentStepConfig.backButtonText || 'Back'}
              variant="ghost"
              onClick={back}
              disabled={isLoading}
            />
          )}

          {!currentStepConfig.hideNextButton && (
            <Button
              content={
                (typeof currentStepConfig.nextButtonText === 'function'
                  ? currentStepConfig.nextButtonText(data)
                  : currentStepConfig.nextButtonText) ||
                (isLastStep ? 'Complete' : 'Next')
              }
              variant="primary"
              onClick={next}
              disabled={!canGoNext || isLoading}
              isLoading={isLoading}
            />
          )}
        </DialogActions>
      </Dialog>
    </MultiStepContext.Provider>
  );
}
